import { Chip } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  LocalShipping as DispatchedIcon,
  RadioButtonUnchecked as OpenIcon,
  Sensors as NodeIcon,
  Flight as HelicopterIcon,
  Person as ManualIcon,
  Business as ThirdPartyIcon,
} from "@mui/icons-material";
import type { SightingType, SightingStatus, DetectionSource } from "../types";

// Type badge
interface TypeBadgeProps {
  type: SightingType;
  size?: "small" | "medium";
}

const typeConfig: Record<SightingType, { label: string; color: "error" | "warning" | "info" | "success" | "secondary" | "default" }> = {
  earth_moving: { label: "Earth Moving", color: "error" },
  road_works: { label: "Road Works", color: "warning" },
  building_works: { label: "Building Works", color: "warning" },
  machinery_parked: { label: "Machinery", color: "info" },
  agi_activity: { label: "AGI Activity", color: "secondary" },
  marker_posts: { label: "Marker Post", color: "default" },
  forestry: { label: "Forestry", color: "success" },
  quarry: { label: "Quarry", color: "error" },
  flooding: { label: "Flooding", color: "info" },
  ditching: { label: "Ditching", color: "info" },
  network_works: { label: "Network Works", color: "success" },
  fencing: { label: "Fencing", color: "default" },
  other: { label: "Other", color: "default" },
  unidentified: { label: "Unidentified", color: "warning" },
};

export const SightingTypeBadge = ({ type, size = "small" }: TypeBadgeProps) => {
  const config = typeConfig[type];
  return <Chip label={config.label} size={size} color={config.color} variant="outlined" />;
};

// Status badge
interface StatusBadgeProps {
  status: SightingStatus;
  size?: "small" | "medium";
}

const statusConfig: Record<SightingStatus, { label: string; color: "error" | "warning" | "success"; icon: React.ReactElement }> = {
  open: { label: "Open", color: "error", icon: <OpenIcon /> },
  dispatched: { label: "Dispatched", color: "warning", icon: <DispatchedIcon /> },
  closed: { label: "Closed", color: "success", icon: <CheckCircleIcon /> },
};

export const SightingStatusBadge = ({ status, size = "small" }: StatusBadgeProps) => {
  const config = statusConfig[status];
  return <Chip icon={config.icon} label={config.label} size={size} color={config.color} variant="outlined" />;
};

// Source badge
interface SourceBadgeProps {
  source: DetectionSource;
  size?: "small" | "medium";
}

const sourceConfig: Record<DetectionSource, { label: string; color: "primary" | "secondary" | "info" | "default"; icon: React.ReactElement }> = {
  node: { label: "Node", color: "primary", icon: <NodeIcon /> },
  helicopter: { label: "Helicopter", color: "secondary", icon: <HelicopterIcon /> },
  manual: { label: "Manual", color: "info", icon: <ManualIcon /> },
  third_party: { label: "Third Party", color: "default", icon: <ThirdPartyIcon /> },
};

export const SightingSourceBadge = ({ source, size = "small" }: SourceBadgeProps) => {
  const config = sourceConfig[source];
  return <Chip icon={config.icon} label={config.label} size={size} color={config.color} variant="outlined" />;
};
